import { Component, inject } from '@angular/core';
import { HxDateRangeSelectComponent, HxLoaderComponent, HxStoreSelectComponent } from 'hx-component';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';
import { DatePipe, DecimalPipe, NgClass, NgForOf, NgIf } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import {
  DateRange,
  extractDateRange,
  extractDefaultStoreIds,
  HxReportService,
  HxUserService,
  isoDate,
  ManagerSalesModel, OrderAction,
  StoreBasicModel,
  toRouterQueryParams,
  UserBasicModel
} from 'hx-services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgOptionComponent, NgSelectComponent } from '@ng-select/ng-select';
import { saveAs } from 'file-saver';
import { HcToastrService } from '@manager-app/service/toastr.service';

@Component({
  selector: 'app-manager-sales',
  templateUrl: './manager-sales.component.html',
  styleUrls: ['./manager-sales.component.css'],
  imports: [
    HxDateRangeSelectComponent,
    HxStoreSelectComponent,
    TranslocoPipe,
    HxLoaderComponent,
    FormsModule,
    NgForOf,
    NgIf,
    DecimalPipe,
    RouterLink,
    NgOptionComponent,
    NgSelectComponent,
    DatePipe,
    NgClass
  ]
})
export class ManagerSalesComponent {
  params: {
    storeIds?: number[],
    date?: DateRange,
    managerUserIds?: number[],
    actions?: OrderAction[]
  } = {};
  managers: UserBasicModel[] = [];
  isLoading = {
    list: false,
    generate: false,
  };
  list: ManagerSalesModel[] = [];
  totalSum = 0;
  totalDiscountSum = 0;
  typeOptions: { id: OrderAction, label: string }[] = [
    {id: OrderAction.CREATED, label: 'action.CREATED'},
    {id: OrderAction.PACKED, label: 'action.PACKED'},
    {id: OrderAction.DECORED, label: 'action.DECORED'},
    {id: OrderAction.WAIT_PAYMENT, label: 'action.WAIT_PAYMENT'},
    {id: OrderAction.SHIPPED, label: 'action.SHIPPED'},
    {id: OrderAction.RECEIVED, label: 'action.RECEIVED'},
    {id: OrderAction.CANCELLED, label: 'action.CANCELLED'},
    {id: OrderAction.REFUNDED, label: 'action.REFUNDED'},
  ];

  private readonly reportService = inject(HxReportService);
  private readonly aRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly userService = inject(HxUserService);
  private readonly toastr = inject(HcToastrService);
  private readonly tr = inject(TranslocoService);

  constructor() {
    this.aRoute.queryParamMap.pipe(takeUntilDestroyed()).subscribe(async paramMap => {
      const asArrFn = (name: string, mapFn: (key: string) => any) => paramMap.get(name)?.split(',').map(mapFn) ?? undefined;
      this.params.date = extractDateRange('date', paramMap) ?? {from: isoDate(new Date()), to: isoDate(new Date())};
      this.params.storeIds = asArrFn('storeIds', str => Number(str)) ?? extractDefaultStoreIds();
      this.params.managerUserIds = asArrFn('managerUserIds', str => Number(str)) ?? [];
      this.params.actions = asArrFn('actions', str => str as OrderAction);
      if (this.params.storeIds) {
        this.loadOperators();
        this.load();
      }
    });
  }

  resetFilter() {
    this.params.date = {from: isoDate(new Date()), to: isoDate(new Date())};
    this.params.managerUserIds = [];
    this.params.actions = [];
    this.navigate();
  }

  navigate() {
    this.router.navigate(['./'], {
      queryParams: toRouterQueryParams(this.params),
      relativeTo: this.aRoute
    });
  }

  onStoreChanged(store?: StoreBasicModel | StoreBasicModel[]) {
    if (Array.isArray(store) && store.length === 0) {
      this.params.storeIds = [];
      this.managers = [];
      this.params.managerUserIds = [];
    }
    this.navigate();
  }

  async generate() {
    this.isLoading.generate = true;
    try {
      const response = await this.reportService.generateManagerSalesReport({
        fromDate: this.params.date.from,
        toDate: this.params.date.to,
        storeIds: this.params.storeIds,
        managerUserIds: this.params.managerUserIds,
        actions: this.params.actions
      });
      const contentDispositionHeader = response.headers.get('Content-Disposition');
      const result = contentDispositionHeader.split(';')[1].trim().split('=')[1];
      const blob = new Blob([response.body], {type: 'application/octet-stream'});
      saveAs(blob, result);
      this.toastr.success(this.tr.translate('received-report.ts.success'));
    } catch (e: any) {
      console.log(e);
    } finally {
      this.isLoading.generate = false;
    }
  }

  private async loadOperators() {
    const mns = await this.userService.getManagersByStoreIds(this.params.storeIds);
    this.managers = Array.from(
      new Map(mns.map(m => [m.id, m])).values()
    );
  }

  private async load() {
    this.list = [];
    this.isLoading.list = true;
    try {
      this.list = await this.reportService.getManagerSalesReport({ fromDate: this.params.date.from, toDate: this.params.date.to, storeIds: this.params.storeIds, managerUserIds: this.params.managerUserIds, actions: this.params.actions });
      this.totalSum = this.list.reduce((s, r) => s + (r.total || 0), 0);
      this.totalDiscountSum = this.list.reduce((s, r) => s + (r.totalDicount || 0), 0);
    } finally {
      this.isLoading.list = false;
    }
  }
}
